import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * @typedef {'in_stock' | 'low_stock' | 'pre_order' | 'out_of_stock'} LocationStockStatus
 */

/**
 * @typedef {Object} LocationStockRow
 * @property {string} name
 * @property {string} [suburbLine]
 * @property {LocationStockStatus} status
 * @property {number} quantity
 */

const STATUS_DOT = {
  in_stock: 'in',
  low_stock: 'low',
  pre_order: 'pre',
  out_of_stock: 'out',
};

class LocationStock extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {HTMLElement | null} */
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    const variantId = this.dataset.variantId;
    if (this.dataset.initialTracked === 'true' && variantId && this.#isTracked(variantId)) {
      this.hidden = false;
      void this.#load(variantId);
    } else {
      this.hidden = true;
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const productId = event.detail.data.productId;
    if (
      productId &&
      this.dataset.productId &&
      productId !== this.dataset.productId &&
      !event.detail.data.newProduct
    ) {
      return;
    }

    if (event.detail.data.newProduct?.id) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    const variantId = event.detail.resource?.id;
    if (variantId == null) return;

    const id = String(variantId);
    if (!this.#isTracked(id)) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.dataset.variantId = id;
    void this.#load(id);
  };

  /**
   * @param {string} variantId
   */
  #isTracked(variantId) {
    const ids = (this.dataset.trackedVariantIds || '').split(',').filter(Boolean);
    return ids.includes(String(variantId));
  }

  /**
   * @param {string} message
   * @param {string} className
   */
  #showMessage(message, className) {
    const list = this.querySelector('[data-location-list]');
    if (!list) return;
    list.replaceChildren();
    const item = document.createElement('li');
    item.className = `location-stock__note ${className}`;
    item.textContent = message;
    list.appendChild(item);
  }

  /**
   * @param {string} variantId
   */
  async #load(variantId) {
    const apiUrl = this.dataset.apiUrl;
    const shop = this.dataset.shop;
    if (!apiUrl || !shop || !variantId) {
      this.#showMessage(this.dataset.errorText || 'Unable to load stock.', 'location-stock__error');
      return;
    }

    this.#abort?.abort();
    const abort = new AbortController();
    this.#abort = abort;
    this.#showMessage(this.dataset.loadingText || 'Checking stock...', 'location-stock__loading');

    const params = new URLSearchParams({
      shop,
      variantId,
      lowStockThreshold: this.dataset.lowThreshold || '5',
    });

    try {
      const response = await fetch(`${apiUrl}?${params.toString()}`, { signal: abort.signal });
      const payload = await response.json();
      if (abort.signal.aborted || this.dataset.variantId !== variantId) return;

      if (!response.ok || !payload.ok) {
        this.#showMessage(this.dataset.errorText || 'Unable to load stock.', 'location-stock__error');
        return;
      }

      if (payload.inventoryTracked === false) {
        this.hidden = true;
        return;
      }

      if (!Array.isArray(payload.locations) || payload.locations.length === 0) {
        this.#showMessage(
          this.dataset.emptyText || 'No locations are stocking this product.',
          'location-stock__empty'
        );
        return;
      }

      this.#render(payload.locations);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#showMessage(this.dataset.errorText || 'Unable to load stock.', 'location-stock__error');
    }
  }

  /**
   * @param {LocationStockStatus} status
   */
  #labelFor(status) {
    if (status === 'in_stock') return this.dataset.labelIn || 'In stock';
    if (status === 'low_stock') return this.dataset.labelLow || 'Low stock';
    if (status === 'pre_order') return this.dataset.labelPre || this.dataset.labelOut || 'On backorder';
    return this.dataset.labelOut || 'Out of stock';
  }

  /**
   * @param {LocationStockRow[]} locations
   */
  #render(locations) {
    const list = this.querySelector('[data-location-list]');
    if (!list) return;
    list.replaceChildren();

    const showQuantity = this.dataset.showQuantity === 'true';

    for (const location of locations) {
      const status = STATUS_DOT[location.status] ? location.status : 'out_of_stock';
      const row = document.createElement('li');
      row.className = 'location-stock__row';

      const place = document.createElement('div');
      place.className = 'location-stock__place';

      const name = document.createElement('p');
      name.className = 'location-stock__name';
      name.textContent = location.name || 'Location';
      place.appendChild(name);

      const suburbLine = location.suburbLine?.trim();
      if (suburbLine) {
        const suburb = document.createElement('p');
        suburb.className = 'location-stock__suburb';
        suburb.textContent = suburbLine;
        place.appendChild(suburb);
      }

      const statusEl = document.createElement('span');
      statusEl.className = `location-stock__badge location-stock__badge--${STATUS_DOT[status]}`;

      const dot = document.createElement('span');
      dot.className = `location-stock__dot location-stock__dot--${STATUS_DOT[status]}`;
      dot.setAttribute('aria-hidden', 'true');

      const label = document.createElement('span');
      const text = this.#labelFor(status);
      const quantity = Number(location.quantity);
      label.textContent =
        showQuantity && Number.isFinite(quantity) ? `${text} (${quantity})` : text;

      statusEl.append(dot, label);
      row.append(place, statusEl);
      list.appendChild(row);
    }
  }
}

if (!customElements.get('location-stock')) {
  customElements.define('location-stock', LocationStock);
}
